/**
 * @file reservations.api.js
 * @module ReservationsAPI
 * @description HTTP API wrapper for reservation-related endpoints (create, read, update, cancel, export, etc.).
 * All methods return an Axios Promise created by axiosInstance(token).
 */

import { axiosInstance } from "./axios";

export const ReservationsAPI = {

    /**
     * Create a new reservation.
     * @param {Object} reservation - Reservation payload.
     * @param {string} [token] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created reservation.
     */
    newReservation: (reservation, token = "") => {
        return axiosInstance(token).post(`/reservations`, { reservation: reservation })
    },

    /**
     * Retrieve reservations with pagination and optional query.
     * @param {number} [limit=20] - Page size.
     * @param {number} [page=1] - Page number.
     * @param {string} [query=""] - Optional query string for filtering.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to reservations list.
     */
    getReservations: (limit = 20, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/reservations?limit=${limit}&page=${page}${query ? `&${query}` : ""}`)
    },

    /**
     * Get metadata for reservations used in filters.
     * @param {string} [query=""] - Optional query to modify metadata response.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to metadata object.
     */
    getMetadataReservations: (query = "", token = "") => {
        return axiosInstance(token).get(`/reservations/metadata/quitFilter${query ? `?${query}` : ""}`)
    },

    /**
     * Get a single reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {boolean} [populate=true] - Whether to populate referenced fields.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to reservation data.
     */
    getReservation: (reservationId, populate = true, token = "") => {
        return axiosInstance(token).get(`/reservations/${reservationId}?populate=${populate}`)
    },

    /**
     * Update an existing reservation.
     * @param {string} reservationId - Reservation identifier.
     * @param {Object} reservation - Updated reservation object.
     * @param {Array} recipients - Email recipients for notifications (optional).
     * @param {string} reason - Reason for update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated reservation.
     */
    updateReservation: (reservationId, reservation, recipients, reason, token = "") => {
        return axiosInstance(token).put(`/reservations/${reservationId}`, { reservation: reservation, recipients: recipients, reason: reason })
    },

    /**
     * Delete a reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion.
     */
    deleteReservation: (reservationId, token = "") => {
        return axiosInstance(token).delete(`/reservations/${reservationId}`)
    },

    /**
     * Delete multiple reservations.
     * @param {Array} [reservations=[]] - Array of reservation ids to delete.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteReservationMany: (reservations = [], token = "") => {
        return axiosInstance(token).put(`/deleteMany/reservations`, { reservations: reservations })
    },

    /**
     * Update reservation status. If responsible is provided, it will be sent as header.
     * @param {string} reservationId - Reservation identifier.
     * @param {Object} payload - Status update payload.
     * @param {string|null} responsible - Optional responsible user id.
     * @param {string} role - Role of the responsible user.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated status.
     */
    updateStatusReservation: (reservationId, payload, responsible, role, token = "") => {
        if (responsible) {
            return axiosInstance(token).put(`/reservations/status/changue/${reservationId}`, payload, { headers: { "responsible": responsible, "role": role } })
        } else {
            return axiosInstance(token).put(`/reservations/status/changue/${reservationId}`, payload)
        }
    },

    /**
     * Cancel a reservation from supplier side (supplier rejects reservation).
     * @param {string} reservationId - Reservation identifier.
     * @param {string} supplierId - Supplier identifier.
     * @param {Object} payload - Cancellation payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to cancellation result.
     */
    canlcelReservation: (reservationId, supplierId, payload, token = "") => {
        return axiosInstance(token).put(`/reservation-reject-supplier/${supplierId}/${reservationId}`, payload);
    },

    /**
     * Retrieve reservation filters; supports pagination objects.
     * @param {string} query - Query string.
     * @param {Object} pagination - Pagination object or empty object.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to filter results.
     */
    reservationsFilters: (query, pagination, token = "") => {
        if (Object.entries(pagination).length === 0) {
            return axiosInstance(token).get(`/filters/reservations?pagination=true&${query}`);
        } else {
            return (pagination.type === "change") ? axiosInstance(token).get(`/filters/reservations?pagination=true&${query}&limit=${limit}&page=${pagination.page}`) : axiosInstance(token).get(`${pagination.url}`);
        }
    },

    /**
     * Search reservations with optional pagination object.
     * @param {string} query - Search query string.
     * @param {Object} pagination - Pagination object or empty object.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to search results.
     */
    reservationsSearch: (query, pagination, token = "") => {
        if (Object.entries(pagination).length === 0) {
            return axiosInstance(token).get(`/search/reservations?search=${query}`);
        } else {
            return (pagination.type === "change") ? axiosInstance(token).get(`/search/reservations?search=${query}&limit=${limit}&page=${pagination.page}`) : axiosInstance(token).get(`${pagination.url}`);
        }
    },

    /**
     * Confirm a reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to confirmation result.
     */
    confimedReservations: (reservationId, token = "") => {
        return axiosInstance(token).get(`/reservations/${reservationId}/confirm`);
    },

    /**
     * Cancel a reservation by id with reason and refund eligibility.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} reason - Cancellation reason.
     * @param {boolean} refundEligibility - Whether refund applies.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to cancellation result.
     */
    canlcelReservationById: (reservationId, reason, refundEligibility, token = "") => {
        return axiosInstance(token).put(`/reservations/${reservationId}/cancel`, { reason: reason, refundEligibility: refundEligibility, source: "rhombuss" })
    },

    /**
     * Supplier confirms reservation action.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} action - Action name.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to confirmation result.
     */
    supplierConfirmed: (reservationId, action, token = "") => {
        return axiosInstance(token).post(`/supplier/confirm/reservations/${reservationId}`, { action: action })
    },

    /**
     * Send a payment link to customers.
     * @param {Object} payload - Payment link payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the send result.
     */
    sendPaymentLink: (payload, token = "") => {
        return axiosInstance(token).post(`/payment-link`, payload)
    },

    /**
     * Get histories for a reservation (paginated).
     * @param {string} reservationId - Reservation identifier.
     * @param {number} limit - Page size.
     * @param {number} page - Page number.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to histories list.
     */
    getHistoriesByReservationId: (reservationId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/reservations/${reservationId}/histories?limit=${limit}&page=${page}`)
    },

    /**
     * Update a history entry by id.
     * @param {string} historyId - History entry id.
     * @param {Object} history - Updated history data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },

    /**
     * Resend reservation-related emails to a list of recipients.
     * @param {string} reservationId - Reservation identifier.
     * @param {Array} recipients - Array of recipient emails or identifiers.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    resendEmailToUsersByReservationId(reservationId, recipients, token = "") {
        return axiosInstance(token).post(`/reservations/${reservationId}/resend-email`, { recipients: recipients })
    },

    /**
     * Mark reservations as paid.
     * @param {Array} reservations - Array of reservation ids to mark as paid.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to operation result.
     */
    markReservationsAsPaid: (reservations, token = "") => {
        return axiosInstance(token).post(`/reservations/mark-as-paid`, { reservations: reservations })
    },

    /**
     * Change reservation status (any status) with reason.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} status - New status.
     * @param {string} reason - Reason for status change.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated reservation.
     */
    changeRservationStatus: (reservationId, status, reason, token = "") => {
        return axiosInstance(token).patch(`/reservations/${reservationId}/updateAnyStatus`, { status: status, reason: reason })
    },

    /**
     * Download quote file for a reservation.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the file (binary/blob).
     */
    getQuoteFile: (reservationId, token = "") => {
        return axiosInstance(token).get(`/reservations/${reservationId}/downloadQuote`)
    },

    /**
     * Get reservations metrics. Optional query parameter support (if present in implementation).
     * @param {string} [query]
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to metrics data.
     */
    getReservationsMetrics: (query, token = "") => {
        return axiosInstance(token).get(`/reservations-metrics${query ? `?${query}` : ""}`)
    },

    /**
     * Create a partial driver profile for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {Object} driver - Driver data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created driver.
     */
    createPartialDriverProfile: (branchId, driver, token = "") => {
        return axiosInstance(token).post(`/branches/${branchId}/drivers`, driver);
    },

    /**
     * Get drivers options for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to drivers options.
     */
    getDriversByBranchId: (branchId, token = "") => {
        return axiosInstance(token).get(`/branches/${branchId}/drivers-options`);
    },

    /**
     * Send notifications to passengers associated with a reservation.
     * @param {string} reservationId - Reservation identifier.
     * @param {Array} passengers - Array of passenger objects/ids to notify.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendNotificationByReservationId: (reservationId, passengers, token = "") => {
        return axiosInstance(token).post(`/reservations/${reservationId}/passengers-notifications`, { passengers: passengers })
    },

    /**
     * Download PDF (quote, voucher or invoice) for a reservation or invoice id.
     * Returns blob responseType for file download.
     * @param {string} pdfType - 'quote'|'voucher'|'invoice'
     * @param {string} identifire - Reservation or invoice identifier.
     * @param {Object} client - Client object with type and _id.
     * @param {string} voucherType - Voucher type (if applicable).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to binary blob.
     */
    downloadPDF: (pdfType, identifire, client, voucherType, token = '') => {
        if (["quote", "voucher"].includes(pdfType)) {
            return axiosInstance(token).get(`/reservations/${identifire}/generate-pdf?pdfType=${pdfType}&userType=${client.type}&userReference=${client._id}&voucherType=${voucherType}`, {
                responseType: 'blob',
            });
        } else {
            return axiosInstance(token).get(`/invoices/${identifire}/generate-pdf?pdfType=invoice&userType=${client.type}&userReference=${client._id}&voucherType=${voucherType}`, {
                responseType: 'blob',
            });
        }
    },

    /**
     * Download ticket PDFs for a reservation.
     * @param {string} identifire - Reservation identifier.
     * @param {Object} client - Client object.
     * @param {number} quantity - Number of tickets.
     * @param {string} ticketReference - Ticket reference id.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to blob file.
     */
    downloadPDFTickets: (identifire, client, quantity, ticketReference, token = '') => {
        return axiosInstance(token).get(`/tickets/${identifire}/generate-pdf?pdfType=tickets&userType=${client.type}&userReference=${client._id}&quantity=${quantity}&ticketReference=${ticketReference}`, {
            responseType: 'blob',
        });
    },

    /**
     * Create reservation in OTA channel.
     * @param {Object} reservation - Reservation payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to OTA reservation.
     */
    otaCreateRervation: (reservation, token = "") => {
        return axiosInstance(token).post(`/ota/reservations`, { reservation: reservation })
    },

    /**
     * Update OTA reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {Object} reservation - Updated reservation.
     * @param {Array} recipients - Email recipients.
     * @param {string} reason - Reason for update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to update result.
     */
    otaUpdateReservation: (reservationId, reservation, recipients, reason, token = "") => {
        return axiosInstance(token).put(`/ota/reservations/${reservationId}`, { reservation: reservation, recipients: recipients, reason: reason })
    },

    /**
     * Confirm OTA reservation by id.
     * @param {string} reservationId
     * @param {Object} payload
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise}
     */
    otaConfirmReservationById: (reservationId, payload, token = '') => {
        return axiosInstance(token).post(`/ota/reservations/${reservationId}/confirm`, payload)
    },

    /**
     * Cancel OTA reservation by id.
     * @param {string} reservationId
     * @param {Object} payload
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise}
     */
    otaCancelReservationById: (reservationId, payload, token = '') => {
        return axiosInstance(token).post(`/ota/reservations/${reservationId}/cancel`, payload)
    },

    /**
     * Get commission by product reference.
     * @param {string} productId - Product identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to commission details.
     */
    getCommissionByReference: (productId, token = "") => {
        return axiosInstance(token).get(`/commissions/reference/${productId}`);
    },

    /**
     * Create shareable reservation link.
     * @param {Object} payload - Payload containing reservation details.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to share link data.
     */
    createShareReservationsLinks: (payload, token = "") => {
        return axiosInstance(token).post(`/shared-links`, payload);
    },

    /**
     * Create shareable reservation list.
     * @param {Object} payload - Payload containing list details.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to shared list data.
     */
    createShareReservationsList: (payload, token = "") => {
        return axiosInstance(token).post(`/shared-list`, payload);
    },
    /**
     * Create multiple transfer reservations from a CSV file upload.
     * @param {File} file - CSV file containing reservations data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to bulk creation result.
     */
    bulkCreateTransferReservations: (file, token = "") => {
        const formData = new FormData();
        formData.append("file", file);
        return axiosInstance(token).post(`/bulk-create-transfer-reservations`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
    }
}
